import { createI18n } from 'vue-i18n'
import { nextTick } from 'vue'

export type Locale = 'en' | 'zh-TW' | 'zh-HK' | 'zh-CN' | 'ja' | 'ko'

export const isValidLocale = (locale: string): locale is Locale => {
  return ['en', 'zh-TW', 'zh-HK', 'zh-CN', 'ja', 'ko'].includes(locale)
}

// Create i18n instance with empty messages initially
const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages: {},
  // Add explicit number format options to ensure consistent numeric display
  numberFormats: {
    'en': {
      numeric: {
        style: 'decimal',
        useGrouping: false
      }
    },
    'zh-TW': {
      numeric: {
        style: 'decimal',
        useGrouping: false
      }
    },
    'zh-HK': {
      numeric: {
        style: 'decimal',
        useGrouping: false
      }
    },
    'zh-CN': {
      numeric: {
        style: 'decimal',
        useGrouping: false
      }
    },
    'ja': {
      numeric: {
        style: 'decimal',
        useGrouping: false
      }
    },
    'ko': {
      numeric: {
        style: 'decimal',
        useGrouping: false
      }
    }
  },
  // Add date and time formats for each locale
  datetimeFormats: {
    'en': {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      shortWithoutYear: {
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      weekday: {
        weekday: 'short'
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      },
      booking: {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      }
    },
    'zh-TW': {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      shortWithoutYear: {
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      weekday: {
        weekday: 'short'
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      },
      booking: {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }
    },
    'zh-HK': {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      shortWithoutYear: {
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      weekday: {
        weekday: 'short'
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      },
      booking: {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }
    },
    'zh-CN': {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      shortWithoutYear: {
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      weekday: {
        weekday: 'short'
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      },
      booking: {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }
    },
    'ja': {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      shortWithoutYear: {
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      weekday: {
        weekday: 'short'
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      },
      booking: {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }
    },
    'ko': {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      shortWithoutYear: {
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      weekday: {
        weekday: 'short'
      },
      time: {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      },
      booking: {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }
    }
  }
})

// Get user's preferred language from localStorage or browser
const getUserLanguage = (): Locale => {
  // First check localStorage for saved user preference
  const savedLocale = localStorage.getItem('user-locale')
  if (savedLocale && isValidLocale(savedLocale)) {
    return savedLocale
  }
  
  // Check navigator.languages (the prioritized languages array)
  if (navigator.languages && navigator.languages.length) {
    // Try to find an exact match first
    for (const lang of navigator.languages) {
      if (isValidLocale(lang)) {
        return lang
      }
      
      // If we have something like 'en-US' but support 'en', use that
      const baseLang = lang.split('-')[0]
      if (isValidLocale(baseLang)) {
        return baseLang
      }
      
      // Handle special language mappings
      const languageMapping: Record<string, Locale> = {
        // Maps browser language codes to our supported locales
        'zh-HK': 'zh-HK',
        'zh-TW': 'zh-TW',
        'zh-CN': 'zh-CN',
        'zh': 'zh-CN', // Default Chinese to Simplified Chinese
        'ja-JP': 'ja',
        'ko-KR': 'ko',
        // iOS-specific codes
        'zh-Hans': 'zh-CN', // iOS Simplified Chinese
        'zh-Hans-CN': 'zh-CN',
        'zh-Hans-HK': 'zh-HK',
        'zh-Hans-MO': 'zh-HK', // Macau uses Traditional Chinese
        'zh-Hans-SG': 'zh-CN', // Singapore uses Simplified
        'zh-Hant': 'zh-TW', // iOS Traditional Chinese
        'zh-Hant-TW': 'zh-TW',
        'zh-Hant-HK': 'zh-HK',
        'zh-Hant-MO': 'zh-HK'
      }
      
      if (languageMapping[lang]) {
        return languageMapping[lang]
      }
    }
  }
  
  // Fallback to navigator.language as before
  const browserLang = navigator.language
  
  // Try iOS-specific language codes for the fallback
  const iOSLanguageMapping: Record<string, Locale> = {
    'zh-Hans': 'zh-CN', // iOS Simplified Chinese
    'zh-Hans-CN': 'zh-CN',
    'zh-Hans-HK': 'zh-HK',
    'zh-Hans-MO': 'zh-HK',
    'zh-Hans-SG': 'zh-CN',
    'zh-Hant': 'zh-TW', // iOS Traditional Chinese
    'zh-Hant-TW': 'zh-TW',
    'zh-Hant-HK': 'zh-HK',
    'zh-Hant-MO': 'zh-HK'
  }
  
  if (iOSLanguageMapping[browserLang]) {
    return iOSLanguageMapping[browserLang]
  }
  
  if (isValidLocale(browserLang)) {
    return browserLang
  }
  
  // Try base language of navigator.language
  const baseLang = browserLang.split('-')[0]
  if (isValidLocale(baseLang)) {
    return baseLang
  }
  
  // Default fallback
  return 'en'
}

// Set initial language
const initialLocale = getUserLanguage()
i18n.global.locale.value = initialLocale as any // Type assertion needed due to vue-i18n typing limitations
document.querySelector('html')?.setAttribute('lang', initialLocale)

// Dynamic language loading function
export const loadLanguageAsync = async (locale: Locale) => {
  const messages = await import(`./locales/${locale}.json`)
  i18n.global.setLocaleMessage(locale, messages.default)
  i18n.global.locale.value = locale as any // Type assertion needed due to vue-i18n typing limitations
  document.querySelector('html')?.setAttribute('lang', locale)
  localStorage.setItem('user-locale', locale)
  return nextTick()
}

export default i18n 

// Initialize with default language
loadLanguageAsync('en').catch(error => {
  console.error('Failed to load default language:', error)
}) 